import { useState } from 'react';
import type { AdminCaseSummary, CaseStatus } from '@casetrace/shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAdminActions } from '../../hooks/use-admin-actions';

const SELECT_CLASS =
  'rounded-md border border-white/[0.1] bg-[#1a1b23] px-3 py-2 text-xs text-[#ededf0]';

interface AdminCaseEditorProps {
  summary: AdminCaseSummary;
}

export function AdminCaseEditor({ summary }: AdminCaseEditorProps) {
  const { updateCase } = useAdminActions();
  const [navn, setNavn] = useState(summary.case.navn);
  const [saksnummer, setSaksnummer] = useState(summary.case.saksnummer);
  const [klient, setKlient] = useState(summary.case.klient);
  const [status, setStatus] = useState<CaseStatus>(summary.case.status);
  const hasChanges =
    navn !== summary.case.navn ||
    saksnummer !== summary.case.saksnummer ||
    klient !== summary.case.klient ||
    status !== summary.case.status;

  async function handleSave() {
    await updateCase.mutateAsync({
      caseId: summary.case.id,
      navn,
      saksnummer,
      klient,
      status,
    });
  }

  return (
    <div className="mt-4 rounded-md border border-white/[0.06] bg-white/[0.03] p-3">
      <h3 className="mb-3 text-xs font-semibold text-text-primary">Administrer sak</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          id={`case-name-${summary.case.id}`}
          label="Navn"
          value={navn}
          onChange={(event) => setNavn(event.target.value)}
        />
        <Input
          id={`case-number-${summary.case.id}`}
          label="Saksnummer"
          value={saksnummer}
          onChange={(event) => setSaksnummer(event.target.value)}
        />
        <Input
          id={`case-client-${summary.case.id}`}
          label="Klient"
          value={klient}
          onChange={(event) => setKlient(event.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-[#8b8d9a]">Status</label>
          <select
            className={SELECT_CLASS}
            value={status}
            onChange={(event) => setStatus(event.target.value as CaseStatus)}
          >
            <option value="active">Aktiv</option>
            <option value="archived">Arkivert</option>
            <option value="closed">Lukket</option>
          </select>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-3">
        {updateCase.isError && (
          <p className="mr-auto text-xs text-danger/80">Kunne ikke lagre sak.</p>
        )}
        <Button
          type="button"
          variant="outline"
          className="px-3 py-1.5 text-xs"
          disabled={!hasChanges || updateCase.isPending}
          onClick={() => void handleSave()}
        >
          Lagre sak
        </Button>
      </div>
    </div>
  );
}
